import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import { ChevronRight } from 'lucide-react';

export interface DropdownMenuItem {
  id: string;
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** true이면 클릭 불가능한 섹션 헤더로 렌더링 */
  isSection?: boolean;
  /** 서브메뉴 항목 — 마우스 호버 시 오른쪽에 펼쳐짐 */
  children?: DropdownMenuItem[];
  /** 우측 단축키 라벨(예: "Ctrl+Z") — 리프 항목 전용, 회색 표기 */
  shortcut?: string;
  /** Checkable items stay open so several providers can be selected in one visit. */
  checked?: boolean;
  keepOpen?: boolean;
}

interface DropdownMenuProps {
  open: boolean;
  x: number;
  y: number;
  items: DropdownMenuItem[];
  onClose: () => void;
  direction?: 'down' | 'up';
  align?: 'left' | 'right';
  width?: number;
  minWidth?: number;
  /** Keep context menus and their submenus reachable near the viewport edges. */
  constrainToViewport?: boolean;
  /** Backdrop layer; menus render one layer above it. */
  zIndex?: number;
}

const Backdrop = styled.div<{ $zIndex: number }>`
  position: fixed;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
`;

const Menu = styled.div<{
  $x: number;
  $y: number;
  $direction: 'down' | 'up';
  $align: 'left' | 'right';
  $width?: number;
  $minWidth?: number;
  $constrained: boolean;
  $zIndex: number;
}>`
  position: fixed;
  top: ${({ $y }) => $y}px;
  left: ${({ $x }) => $x}px;
  z-index: ${({ $zIndex }) => $zIndex + 1};
  width: ${({ $width, $constrained }) => ($width ? `${$width}px` : $constrained ? 'max-content' : 'auto')};
  min-width: ${({ $minWidth, $constrained }) => $constrained
    ? `min(${$minWidth ?? 180}px, calc(100vw - 16px))`
    : `${$minWidth ?? 180}px`};
  max-width: calc(100vw - 16px);
  padding: 6px;
  box-sizing: border-box;
  ${({ $constrained }) => $constrained && css`
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    overscroll-behavior: contain;
  `}
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40,40,42,0.92)' : 'rgba(255,255,255,0.92)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};

  ${({ $direction, $align }) => {
    const tx = $align === 'right' ? '-100%' : '0';
    const ty = $direction === 'up' ? '-100%' : '0';
    return css`
      transform: translate(${tx}, ${ty});
    `;
  }}
`;

const ItemWrapper = styled.div`
  position: relative;
`;

const ItemButton = styled.button<{ $danger?: boolean; $hasChildren?: boolean; $focused?: boolean }>`
  position: relative;
  width: 100%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 36px;
  padding: 6px 10px;
  border-radius: 8px;
  color: ${({ theme, $danger }) => ($danger ? theme.colors.error : theme.colors.textPrimary)};
  cursor: pointer;
  font-size: 0.8125rem;
  text-align: left;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
  }

  ${({ $focused, theme }) =>
    $focused &&
    css`
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.04)'};
    `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ShortcutLabel = styled.span`
  margin-left: auto;
  padding-left: 16px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.72rem;
  white-space: nowrap;
`;

const ToggleIndicator = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding-left: 12px;
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  &::after {
    content: '';
    width: 28px;
    height: 16px;
    border-radius: 99px;
    background: ${({ $checked, theme }) => $checked ? theme.colors.brand : theme.colors.borderInput};
  }
  &::before {
    content: '';
    position: absolute;
    right: ${({ $checked }) => $checked ? '13px' : '25px'};
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.brandContrastText};
    z-index: 1;
    transition: right 150ms ease;
  }
`;

const ChevronIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-left: auto;
`;

const SubMenu = styled.div<{
  $direction: 'up' | 'down';
  $checkable: boolean;
  $position?: { x: number; y: number };
  $zIndex: number;
}>`
  position: ${({ $position }) => $position ? 'fixed' : 'absolute'};
  left: ${({ $position }) => $position ? `${$position.x}px` : '100%'};
  ${({ $direction, $position }) => $position
    ? css`top: ${$position.y}px;`
    :
    $direction === 'up'
      ? css`bottom: 0;`
      : css`top: 0;`}
  min-width: 170px;
  ${({ $checkable }) => $checkable && css`min-width: 280px;`}
  ${({ $position }) => $position && css`
    width: max-content;
    min-width: min(170px, calc(100vw - 16px));
    max-width: calc(100vw - 16px);
    box-sizing: border-box;
    overscroll-behavior: contain;
  `}
  ${({ $checkable, $position }) => $checkable && $position && css`
    min-width: min(280px, calc(100vw - 16px));
  `}
  max-height: ${({ $position }) => $position ? 'calc(100vh - 16px)' : '420px'};
  overflow-y: auto;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(40,40,42,0.92)' : 'rgba(255,255,255,0.92)'};
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 8px 32px rgba(0,0,0,0.45), 0 1px 3px rgba(0,0,0,0.3)'
      : '0 8px 32px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)'};
  z-index: ${({ $zIndex }) => $zIndex + 2};
`;

const VIEWPORT_MARGIN = 8;
const clampToViewport = (position: number, size: number, viewportSize: number) =>
  Math.max(VIEWPORT_MARGIN, Math.min(position, viewportSize - size - VIEWPORT_MARGIN));

function revealFocusedItem(container: HTMLElement | null) {
  const focused = container?.querySelector<HTMLElement>('[data-menu-focused="true"]');
  if (!container || !focused) return;
  const bounds = container.getBoundingClientRect();
  const itemBounds = focused.getBoundingClientRect();
  if (itemBounds.top < bounds.top) container.scrollTop -= bounds.top - itemBounds.top;
  if (itemBounds.bottom > bounds.bottom) container.scrollTop += itemBounds.bottom - bounds.bottom;
}

const SectionHeader = styled.div`
  padding: 6px 10px 4px;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: none;
  letter-spacing: 0;
  user-select: none;
`;

/* ---- 서브메뉴가 있는 아이템 ---- */

const SubMenuItem: React.FC<{
  item: DropdownMenuItem;
  direction: 'up' | 'down';
  onClose: () => void;
  isPinned: boolean;
  hasClickPinnedSubmenu: boolean;
  onHoverPin: () => void;
  onClickPin: () => void;
  isTopFocused: boolean;
  focusedChildIndex: number | null;
  onFocusTop: () => void;
  onFocusChild: (index: number) => void;
  constrainToViewport: boolean;
  zIndex: number;
  parentPosition: { x: number; y: number };
}> = ({
  item,
  direction,
  onClose,
  isPinned,
  hasClickPinnedSubmenu,
  onHoverPin,
  onClickPin,
  isTopFocused,
  focusedChildIndex,
  onFocusTop,
  onFocusChild,
  constrainToViewport,
  zIndex,
  parentPosition,
}) => {
  const [hovered, setHovered] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const subMenuOpen = !item.disabled && (isPinned || (!hasClickPinnedSubmenu && hovered));

  useLayoutEffect(() => {
    if (!constrainToViewport || !subMenuOpen) return;
    const update = () => {
      const anchor = wrapperRef.current?.getBoundingClientRect();
      const menu = submenuRef.current?.getBoundingClientRect();
      if (!anchor || !menu) return;
      const next = {
        x: clampToViewport(
          anchor.right + menu.width <= window.innerWidth - VIEWPORT_MARGIN
            ? anchor.right : anchor.left - menu.width,
          menu.width, window.innerWidth,
        ),
        y: clampToViewport(direction === 'up' ? anchor.bottom - menu.height : anchor.top,
          menu.height, window.innerHeight),
      };
      setPosition(previous => previous.x === next.x && previous.y === next.y ? previous : next);
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [constrainToViewport, subMenuOpen, direction, item.children, parentPosition]);

  useLayoutEffect(() => {
    if (constrainToViewport) revealFocusedItem(submenuRef.current);
  }, [constrainToViewport, focusedChildIndex]);

  const submenu = subMenuOpen && item.children ? (
    <SubMenu ref={submenuRef} role="menu" aria-label={item.label} $direction={direction}
      $checkable={item.children.some(child => child.checked !== undefined)}
      $position={constrainToViewport ? position : undefined} $zIndex={zIndex}>
      {item.children.map((child, index) =>
        child.isSection ? (
          <SectionHeader key={child.id}>{child.label}</SectionHeader>
        ) : (
          <ItemButton
            key={child.id} role={child.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
            aria-checked={child.checked}
            $danger={child.danger}
            disabled={child.disabled}
            $focused={focusedChildIndex === index}
            data-menu-focused={focusedChildIndex === index}
            onMouseEnter={() => {
              onFocusTop();
              onFocusChild(index);
            }}
            onClick={() => {
              if (child.disabled) return;
              child.onClick();
              if (!child.keepOpen) onClose();
            }}
          >
            {child.icon}
            <ItemLabel>{child.label}</ItemLabel>
            {child.checked !== undefined && <ToggleIndicator $checked={child.checked} aria-hidden="true">{child.checked ? 'ON' : 'OFF'}</ToggleIndicator>}
          </ItemButton>
        ),
      )}
    </SubMenu>
  ) : null;

  return (
    <ItemWrapper
      ref={wrapperRef}
      onMouseEnter={() => {
        setHovered(true);
        onFocusTop();
        if (!item.disabled) {
          onHoverPin();
        }
      }}
      onMouseLeave={() => setHovered(false)}
    >
      <ItemButton
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={subMenuOpen}
        $danger={item.danger}
        disabled={item.disabled}
        $hasChildren
        $focused={isTopFocused}
        data-menu-focused={isTopFocused && focusedChildIndex === null}
        onClick={() => {
          if (item.disabled) return;
          onClickPin();
        }}
      >
        {item.icon}
        <ItemLabel>{item.label}</ItemLabel>
        <ChevronIcon><ChevronRight size={14} /></ChevronIcon>
      </ItemButton>
      {constrainToViewport && submenu ? ReactDOM.createPortal(submenu, document.body) : submenu}
    </ItemWrapper>
  );
};

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  open,
  x,
  y,
  items,
  onClose,
  direction = 'down',
  align = 'left',
  width,
  minWidth,
  constrainToViewport = false,
  zIndex = 120,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pinnedSubmenuId, setPinnedSubmenuId] = useState<string | null>(null);
  const [pinnedByClick, setPinnedByClick] = useState(false);
  const [focusedParentIndex, setFocusedParentIndex] = useState<number>(-1);
  const [focusedChildIndex, setFocusedChildIndex] = useState<number | null>(null);
  const [position, setPosition] = useState({ x, y });

  useLayoutEffect(() => {
    if (!open || !constrainToViewport) return;
    const update = () => {
      const bounds = menuRef.current?.getBoundingClientRect();
      if (!bounds) return;
      const next = {
        x: clampToViewport(align === 'right' ? x - bounds.width : x, bounds.width, window.innerWidth),
        y: clampToViewport(direction === 'up' ? y - bounds.height : y, bounds.height, window.innerHeight),
      };
      setPosition(previous => previous.x === next.x && previous.y === next.y ? previous : next);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [open, constrainToViewport, x, y, align, direction, items, width, minWidth]);

  useLayoutEffect(() => {
    if (constrainToViewport) revealFocusedItem(menuRef.current);
  }, [constrainToViewport, focusedParentIndex]);

  const isNavigableItem = useCallback((item: DropdownMenuItem) => {
    if (item.isSection) return false;
    if (item.disabled) return false;
    return true;
  }, []);

  const getNavigableParentIndices = useCallback(() => {
    const indices: number[] = [];
    items.forEach((item, idx) => {
      if (isNavigableItem(item)) indices.push(idx);
    });
    return indices;
  }, [items, isNavigableItem]);

  const getNavigableChildIndices = useCallback((parentIdx: number) => {
    const parent = items[parentIdx];
    if (!parent?.children?.length) return [] as number[];
    const indices: number[] = [];
    parent.children.forEach((child, idx) => {
      if (isNavigableItem(child)) indices.push(idx);
    });
    return indices;
  }, [items, isNavigableItem]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      setPinnedSubmenuId(null);
      setPinnedByClick(false);
      setFocusedParentIndex(-1);
      setFocusedChildIndex(null);
    }
  }, [open]);

  const navigableParentsRef = useRef(getNavigableParentIndices);
  navigableParentsRef.current = getNavigableParentIndices;

  useEffect(() => {
    if (!open) return;
    const parentIndices = navigableParentsRef.current();
    setFocusedParentIndex(parentIndices[0] ?? -1);
    setFocusedChildIndex(null);
    setPinnedSubmenuId(null);
    setPinnedByClick(false);
    const raf = window.requestAnimationFrame(() => {
      menuRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  const focusParentIndex = useCallback((idx: number) => {
    setFocusedParentIndex(idx);
    setFocusedChildIndex(null);
  }, []);

  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Tab') {
      onClose();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
      return;
    }

    const parentIndices = getNavigableParentIndices();
    if (parentIndices.length === 0) return;
    const currentParent = parentIndices.includes(focusedParentIndex)
      ? focusedParentIndex
      : parentIndices[0];
    const currentItem = items[currentParent];
    const childIndices = getNavigableChildIndices(currentParent);
    const inChildMode =
      focusedChildIndex !== null && childIndices.includes(focusedChildIndex);
    const isSelectKey = e.key === 'Enter' || e.key === ' ';

    if (inChildMode) {
      const childPos = childIndices.indexOf(focusedChildIndex);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = childIndices[(childPos + 1) % childIndices.length];
        setFocusedChildIndex(next ?? null);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = childIndices[(childPos - 1 + childIndices.length) % childIndices.length];
        setFocusedChildIndex(next ?? null);
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setFocusedChildIndex(null);
        return;
      }
      if (isSelectKey) {
        e.preventDefault();
        const child = currentItem.children?.[focusedChildIndex];
        if (child && isNavigableItem(child)) {
          child.onClick();
          if (!child.keepOpen) onClose();
        }
      }
      return;
    }

    const parentPos = parentIndices.indexOf(currentParent);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = parentIndices[(parentPos + 1) % parentIndices.length];
      focusParentIndex(next);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = parentIndices[(parentPos - 1 + parentIndices.length) % parentIndices.length];
      focusParentIndex(next);
      return;
    }
    if (e.key === 'ArrowRight') {
      if (childIndices.length > 0) {
        e.preventDefault();
        setPinnedSubmenuId(currentItem.id);
        setFocusedChildIndex(childIndices[0] ?? null);
      }
      return;
    }
    if (isSelectKey) {
      e.preventDefault();
      if (childIndices.length > 0) {
        setPinnedSubmenuId(currentItem.id);
        setFocusedChildIndex(childIndices[0] ?? null);
      } else if (isNavigableItem(currentItem)) {
        currentItem.onClick();
        if (!currentItem.keepOpen) onClose();
      }
    }
  }, [
    focusedChildIndex,
    focusedParentIndex,
    focusParentIndex,
    getNavigableChildIndices,
    getNavigableParentIndices,
    isNavigableItem,
    items,
    onClose,
  ]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      <Backdrop $zIndex={zIndex} onClick={onClose} />
      <Menu
        ref={menuRef}
        role="menu"
        tabIndex={-1}
        onKeyDown={handleMenuKeyDown}
        $x={constrainToViewport ? position.x : x}
        $y={constrainToViewport ? position.y : y}
        $direction={constrainToViewport ? 'down' : direction}
        $align={constrainToViewport ? 'left' : align}
        $width={width}
        $minWidth={minWidth}
        $constrained={constrainToViewport}
        $zIndex={zIndex}
      >
        {items.map((item, index) =>
          item.children && item.children.length > 0 ? (
            <SubMenuItem
              key={item.id}
              item={item}
              direction={direction}
              constrainToViewport={constrainToViewport}
              zIndex={zIndex}
              parentPosition={position}
              onClose={onClose}
              isPinned={pinnedSubmenuId === item.id}
              hasClickPinnedSubmenu={pinnedByClick && !!pinnedSubmenuId}
              onHoverPin={() => {
                if (pinnedByClick) return;
                setPinnedSubmenuId(item.id);
              }}
              onClickPin={() => {
                setPinnedSubmenuId(item.id);
                setPinnedByClick(true);
              }}
              isTopFocused={focusedParentIndex === index}
              focusedChildIndex={focusedParentIndex === index ? focusedChildIndex : null}
              onFocusTop={() => focusParentIndex(index)}
              onFocusChild={(childIndex) => {
                setFocusedParentIndex(index);
                setFocusedChildIndex(childIndex);
              }}
            />
          ) : (
            <ItemButton
              key={item.id}
              role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              aria-checked={item.checked}
              $danger={item.danger}
              disabled={item.disabled}
              $focused={focusedParentIndex === index}
              data-menu-focused={focusedParentIndex === index}
              onMouseEnter={() => {
                focusParentIndex(index);
              }}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                if (!item.keepOpen) onClose();
              }}
            >
              {item.icon}
              <ItemLabel>{item.label}</ItemLabel>
              {item.checked !== undefined && <ToggleIndicator $checked={item.checked} aria-hidden="true">{item.checked ? 'ON' : 'OFF'}</ToggleIndicator>}
              {item.shortcut && <ShortcutLabel>{item.shortcut}</ShortcutLabel>}
            </ItemButton>
          ),
        )}
      </Menu>
    </>,
    document.body,
  );
};
